import { useParams, Link } from "react-router";
import { trpc } from "@/admin/lib/trpc";
import { Button } from "@nosible/shared/components/ui/button";
import { Label } from "@nosible/shared/components/ui/label";
import { EmailPreview } from "../components/EmailPreview";
import { PaginatedEmailList } from "../components/PaginatedEmailList";
import { Briefcase, ChevronLeft, Clock, CheckCircle, User, Mail, Eye } from "lucide-react";
import { useState } from "react";

export function AdminEmailJob() {
  const { jobId } = useParams<{ jobId: string }>();
  const [showPreview, setShowPreview] = useState(false);

  const { data, isLoading, error } = trpc.email.getEmailJob.useQuery(
    { jobId: jobId! },
    { enabled: !!jobId }
  );

  if (!jobId) {
    return (
      <div className="p-6">
        <div className="p-4 bg-signal-blue/20 rounded-lg text-silica">
          No job ID provided
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-chromium-mist">Loading job details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-signal-blue/20 rounded-lg text-silica">
          Error loading job: {error.message}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="p-4 bg-signal-blue/20 rounded-lg text-silica">
          Job not found
        </div>
      </div>
    );
  }

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (startedAt: Date | string, completedAt: Date | string | null) => {
    if (!completedAt) return "In Progress";
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const getJobStatus = (completedAt: Date | string | null) => {
    if (completedAt) {
      return { label: "Completed", icon: CheckCircle, color: "text-neon-green" };
    }
    return { label: "In Progress", icon: Clock, color: "text-chromium-mist" };
  };

  const status = getJobStatus(data.job.completedAt);
  const StatusIcon = status.icon;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/emails/jobs">
          <Button variant="outline" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Jobs
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-silica font-display flex items-center gap-2 mt-4">
          <Briefcase className="h-6 w-6" />
          {data.job.name}
        </h1>
        <p className="text-chromium-mist mt-1">Email job details and individual email status</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Job Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className="bg-slate-core rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-silica font-medium text-lg">Job Status</Label>
              <div className="flex items-center gap-2">
                <StatusIcon className={`h-5 w-5 ${status.color}`} />
                <span className={`text-sm font-medium ${status.color}`}>
                  {status.label}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-chromium-mist">Started:</span>
                <p className="text-silica font-medium">
                  {formatDate(data.job.startedAt)}
                </p>
              </div>
              <div>
                <span className="text-chromium-mist">Completed:</span>
                <p className="text-silica font-medium">
                  {data.job.completedAt ? formatDate(data.job.completedAt) : "In Progress"}
                </p>
              </div>
              <div>
                <span className="text-chromium-mist">Duration:</span>
                <p className="text-silica font-medium">
                  {formatDuration(data.job.startedAt, data.job.completedAt)}
                </p>
              </div>
              <div>
                <span className="text-chromium-mist">Created By:</span>
                <div className="flex items-center gap-1 text-silica font-medium">
                  <User className="h-3 w-3" />
                  {data.job.creatorName || data.job.creatorEmail}
                </div>
              </div>
            </div>
          </div>

          {/* Email Stats */}
          <div className="bg-slate-core rounded-lg p-6">
            <Label className="text-silica font-medium text-lg mb-4 block">Email Statistics</Label>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-silica mb-1">
                  {data.emailCounts.total}
                </div>
                <div className="text-xs text-chromium-mist uppercase">Total</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-neon-green mb-1">
                  {data.emailCounts.sent}
                </div>
                <div className="text-xs text-chromium-mist uppercase">Sent</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-signal-blue mb-1">
                  {data.emailCounts.failed}
                </div>
                <div className="text-xs text-chromium-mist uppercase">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-chromium-mist mb-1">
                  {data.emailCounts.pending}
                </div>
                <div className="text-xs text-chromium-mist uppercase">Pending</div>
              </div>
            </div>
          </div>

          {/* Email Details */}
          <div className="bg-slate-core rounded-lg p-6">
            <Label className="text-silica font-medium text-lg mb-4 block">Email Content</Label>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-chromium-mist">Subject:</span>
                <p className="text-silica font-medium">{data.job.subject}</p>
              </div>
              <div>
                <Button
                  onClick={() => setShowPreview(!showPreview)}
                  variant="outline"
                  size="sm"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Column */}
        {showPreview && (
          <div className="lg:col-span-1">
            <div className="bg-slate-core rounded-lg p-4 sticky top-6">
              <Label className="text-silica font-medium mb-3 block">
                <Eye className="h-4 w-4 inline mr-2" />
                Email Preview
              </Label>
              <EmailPreview html={data.job.html} className="h-[500px]" />
            </div>
          </div>
        )}
      </div>

      {/* Individual Emails */}
      <div className="bg-slate-core rounded-lg p-6">
        <Label className="text-silica font-medium text-lg mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Individual Emails
        </Label>
        <PaginatedEmailList jobId={jobId} />
      </div>
    </div>
  );
}
